import { useState, useEffect, useContext, useRef, useMemo } from "react";
import Box from "@mui/material/Box";
import { Loading, useRedirect } from "react-admin";
import { StoreContext } from "../../store/Store";
import NotificationMessage, {
  notificationType,
} from "../../components/NotificationMessage/NotificationMessage";
import { useParams } from "react-router";
import { APIContext } from "../../store/APIStore";
import { analytics as classes } from "./styles";
import { DASHBOARD_TYPES } from "./constants";
import apiCalls from "../../Configs/config";
import FdTypography from "../../components/UIComponents/Typography/FdTypography";
import DashboardShareMenuDialog from "./DashboardShareMenuDialog";
import { BiLinkAlt } from "react-icons/bi";
import FdButton from "../../components/UIComponents/Button/FdButton";
import { routePaths } from "../../baseLayout/SidebarMenu";
import FloatingChat from "../../components/FloatingChat/FloatingChat";
import { useHasPermissions } from "../../hooks/useHasPermissions";
import { PERMISSIONS } from "../../roles";

const Analytics = () => {
  const [dashboardEmbedUrl, setDashboardEmbedUrl] = useState<string>("");
  const [isFdInsightsActive, setIsFdInsightsActive] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const { eventId } = useParams() as any;
  const [state, dispatch] = useContext(StoreContext);
  const api = useContext(APIContext);
  const notificationRef = useRef<any>();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const redirect = useRedirect();
  const [publicSharingUrl, setPublicSharingUrl] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const chatbotIframeRef = useRef<any>();
  const canManageFdInsights = useHasPermissions(PERMISSIONS.CAN_MANAGE_FD_INSIGHTS);

  const sendEventToken = () => {
    const token = localStorage.getItem("fielddrive-token");
    const session = JSON.parse(localStorage.getItem("fielddrive-session") ?? "{}");
    const userId = session.userId;
    const accountId = state.currentUser.account.id;

    console.log("[sendEventToken] called", {
      iframeRef: chatbotIframeRef.current,
      token: !!token,
      userId,
      accountId,
      eventId,
    });

    if (!token || !userId || !accountId || !eventId || !process.env.REACT_APP_ANALYTICS_CHATBOAT_URL) {
      console.warn("[sendEventToken] early return — missing:", { token: !!token, userId, accountId, eventId, chaturl: process.env.REACT_APP_ANALYTICS_CHATBOAT_URL });
      return;
    }

    console.log("[sendEventToken] posting message to", process.env.REACT_APP_ANALYTICS_CHATBOAT_URL);
    chatbotIframeRef.current?.contentWindow?.postMessage(
      {
        type: "EVENT_TOKEN",
        userId: String(userId),
        eventId: Number(eventId),
        accountId: Number(accountId),
        token,
      },
      process.env.REACT_APP_ANALYTICS_CHATBOAT_URL
    );
  };

  // safety net to stop loading state in case iframe onLoad doesn't fire
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 12000);
    return () => clearTimeout(timer);
  }, []);

  // Respond to handshake messages from the chatbot iframe
  useEffect(() => {
    if (!process.env.REACT_APP_ANALYTICS_CHATBOAT_URL) return;
    const handler = (e: MessageEvent) => {
      if (e.origin !== process.env.REACT_APP_ANALYTICS_CHATBOAT_URL) return;
      sendEventToken();
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [state.selectedEvent, eventId]);

  // If iframe is already mounted and selectedEvent arrives late, push the token
  useEffect(() => {
    if (state.selectedEvent && chatbotIframeRef.current) {
      sendEventToken();
    }
  }, [state.selectedEvent]);

  useEffect(() => {
    // fetch the selected event if it wasn't set in the store
    if (eventId && !state.selectedEvent) {
      fetchSelectedEvent();
    }
  }, []);

  const fetchSelectedEvent = async () => {
    try {
      setLoading(true);
      const event = await api.eventService.getEvent({ id: eventId });
      dispatch({ type: "SET_SELECTED_EVENT", payload: event });
    } catch (error: any) {
      notificationRef.current?.show(
        error?.response?.data?.message,
        notificationType.Error
      );
    }
  };

  useEffect(() => {
    if (apiCalls && state.currentUser) fetchAnalyticsDashboardEmbedUrl();
    fetchAnalyticsPublicSharingUrl();
  }, [apiCalls, state.currentUser]);

  const fetchAnalyticsDashboardEmbedUrl = async () => {
    try {
      setLoading(true);
      const { dashboardEmbedUrl: analyticsEmbedUrl, isFdInsightsActive } =
        await api.analyticsService.getAnalyticsDashboardEmbedUrl(
          DASHBOARD_TYPES.FD_ANALYTICS_V1,
          eventId
        );
      const isFdInsightsActiveFlag = Boolean(isFdInsightsActive) && Boolean(canManageFdInsights);
      setDashboardEmbedUrl(analyticsEmbedUrl as string);
      setIsFdInsightsActive(isFdInsightsActiveFlag);
      await fetchAnalyticsPublicSharingUrl();
    } catch (error: any) {
      notificationRef.current?.show(
        error?.response?.data?.message,
        notificationType.Error
      );
    }
  };

  const fetchAnalyticsPublicSharingUrl = async () => {
    try {
      const { public_url: publicUrl } =
        await api.analyticsSettingsService.fetchPublicUrl(eventId);
      setPublicSharingUrl(publicUrl as string);
    } catch (error: any) {
      notificationRef.current?.show(
        error?.response?.data?.message,
        notificationType.Error
      );
    }
  };

  const generatePublicSharingToken = async () => {
    try {
      const { public_url: publicUrl } =
        await api.analyticsSettingsService.generatePublicSharingToken(
          eventId
        );
      setPublicSharingUrl(publicUrl as string);
    } catch (error: any) {
      notificationRef.current?.show(
        error?.response?.data?.message,
        notificationType.Error
      );
    }
  };

  const publicSharingMenuDialog = (event: any) => {
    if (!publicSharingUrl) generatePublicSharingToken();
    setAnchorEl(event.currentTarget);
  };

  const closePublicSharingMenuDialog = () => {
    setAnchorEl(null);
  };

  if (!eventId || eventId === "undefined") {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        paddingTop="50px"
      >
        Please, select an event!
      </Box>
    );
  }
  return (
    <>
      <Box sx={classes.wrapper}>
        <Box sx={classes.contentWrapper}>
          {dashboardEmbedUrl && (
            <>
              <Box sx={classes.topActions}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <FdTypography variant="h3" label="" />
                </Box>
                <Box
                  textAlign="right"
                  paddingRight="20px"
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <FdButton
                    variant="text"
                    color="primary"
                    onClick={publicSharingMenuDialog}
                    startIcon={<BiLinkAlt />}
                    ButtonName={
                      publicSharingUrl ? "Public link" : "Generate public link"
                    }
                  />
                  <DashboardShareMenuDialog
                    anchorEl={anchorEl}
                    publicSharingUrl={publicSharingUrl}
                    setPublicSharingUrl={setPublicSharingUrl}
                    closePublicSharingMenuDialog={closePublicSharingMenuDialog}
                  />
                </Box>
              </Box>
              <Box sx={{ height: "100%", paddingTop: "20px" }}>
                {loading && <Loading />}
                <iframe
                  ref={iframeRef}
                  src={dashboardEmbedUrl}
                  width="100%"
                  style={{ border: 0 }}
                  height="100%"
                  allowTransparency
                  onLoad={() => setLoading(false)}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
      <NotificationMessage ref={notificationRef} />
      {
        isFdInsightsActive ?
          <FloatingChat
            iframeRef={chatbotIframeRef}
            title="Chat"
            iframeSrc={`${process.env.REACT_APP_ANALYTICS_CHATBOAT_URL}`}
            onIframeLoad={sendEventToken}
            defaultOpen />
          : ('')
      }
    </>
  );
};

export default Analytics;